import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { brl, fmtDate } from "../lib/format";
import {
  Package, Truck, Clock, Prohibit, MagnifyingGlass, Eye, X, Check,
  ArrowClockwise, MapPin, PaperPlaneTilt, Printer, WhatsappLogo, FilePdf, Image as ImageIcon,
} from "@phosphor-icons/react";

const STATUS_STYLE = {
  pending: { label: "Pendente", color: "bg-brand-yellow", icon: Clock },
  shipped: { label: "Enviado", color: "bg-brand-success text-white", icon: Truck },
  canceled: { label: "Cancelado", color: "bg-brand-peach", icon: Prohibit },
};

function trackingUrl(carrier, code) {
  const c = (carrier || "correios").toLowerCase();
  const clean = (code || "").trim().toUpperCase().replace(/\s+/g, "");
  if (!clean) return null;
  if (c.includes("jadlog")) return `https://www.jadlog.com.br/tracking?cte=${encodeURIComponent(clean)}`;
  if (c.includes("loggi")) return `https://www.loggi.com/rastreador/?tracking=${encodeURIComponent(clean)}`;
  if (c.includes("total")) return `https://www.totalexpress.com.br/rastreamento/?codigo=${encodeURIComponent(clean)}`;
  return `https://rastreamento.correios.com.br/app/index.php?objeto=${encodeURIComponent(clean)}`;
}

function normalizePhone(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  // Add BR country code if missing (10 or 11 digits → prepend 55)
  return digits.length <= 11 ? `55${digits}` : digits;
}

function friendlyFirstName(request) {
  const raw = (request.customer_full_name || "").trim();
  if (raw) {
    const first = raw.split(/\s+/)[0];
    // Skip generic single-word placeholders that look robotic in a WhatsApp greeting
    if (!/^cliente$|^customer$|^user$|^usuario$|^usu[áa]ria$/i.test(first)) return first;
  }
  const handle = (request.handle || "").replace(/^@/, "");
  return handle || "cliente";
}

function notifyWhatsapp(request) {
  const phone = normalizePhone(request.customer_whatsapp || request.customer_phone);
  const carrier = request.carrier || "Correios";
  const code = request.tracking_code || "";
  const url = trackingUrl(carrier, code);
  const name = friendlyFirstName(request);
  const lines = [
    `Oi ${name}! 💌 Seu pacote acabou de ser enviado.`,
    "",
    `📦 Transportadora: ${carrier}`,
  ];
  if (code) lines.push(`🔖 Código de rastreio: ${code}`);
  if (url) lines.push(`🔗 Acompanhe: ${url}`);
  lines.push("", "Qualquer dúvida me chama por aqui! ✨");
  const msg = encodeURIComponent(lines.join("\n"));
  if (!phone) {
    toast.info("Cliente sem telefone salvo — abrindo o WhatsApp para você colar em qualquer contato.");
  }
  const wa = phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
  window.open(wa, "_blank", "noopener");
}

export default function ExpeditionAdmin() {
  const [tab, setTab] = useState("pending");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [handleFilter, setHandleFilter] = useState("");
  const [status, setStatus] = useState("");
  const [requests, setRequests] = useState([]);
  const [pendingHandles, setPendingHandles] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (handleFilter.trim()) params.handle = handleFilter.trim();
      if (status) params.status = status;
      const [reqs, hs] = await Promise.all([
        api.get("/expedition/admin/requests", { params }),
        api.get("/expedition/admin/pending-handles"),
      ]);
      setRequests(reqs.data);
      setPendingHandles(hs.data);
      window.dispatchEvent(new CustomEvent("expedition-changed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar");
    }
    setLoading(false);
  }, [dateFrom, dateTo, handleFilter, status]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (rid) => {
    try {
      const { data } = await api.get(`/expedition/admin/requests/${rid}`);
      setDetail(data);
    } catch { toast.error("Erro ao abrir detalhes"); }
  };

  const filteredRequests = useMemo(
    () => tab === "pending" ? requests.filter(r => r.status === "pending") : requests,
    [requests, tab]
  );

  const totals = useMemo(() => {
    return {
      pending: requests.filter(r => r.status === "pending").length,
      shipped: requests.filter(r => r.status === "shipped").length,
      canceled: requests.filter(r => r.status === "canceled").length,
    };
  }, [requests]);

  return (
    <div data-testid="expedition-admin-page" className="space-y-6">
      <header>
        <p className="label-brutal">Administração</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black">Expedição · Painel</h1>
        <p className="text-brand-ink2 mt-2">Gerencie solicitações de envio dos clientes por período e @arroba.</p>
      </header>

      <div className="card-brutal p-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_1.5fr_1fr_auto] gap-3 items-end bg-brand-yellow">
        <div>
          <label className="label-brutal">De</label>
          <input data-testid="admin-date-from" type="date" className="input-brutal" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="label-brutal">Até</label>
          <input data-testid="admin-date-to" type="date" className="input-brutal" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div>
          <label className="label-brutal">@arroba</label>
          <input data-testid="admin-handle-filter" className="input-brutal" placeholder="@cliente" value={handleFilter} onChange={(e) => setHandleFilter(e.target.value)} />
        </div>
        <div>
          <label className="label-brutal">Status</label>
          <select data-testid="admin-status-filter" className="input-brutal" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">— todos —</option>
            <option value="pending">Pendentes</option>
            <option value="shipped">Enviados</option>
            <option value="canceled">Cancelados</option>
          </select>
        </div>
        <button data-testid="admin-refresh" onClick={load} className="btn-white h-[52px]"><ArrowClockwise weight="bold" size={16} /> Atualizar</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard label="Pendentes" value={totals.pending} icon={Clock} bg="bg-brand-yellow" />
        <StatCard label="Enviados" value={totals.shipped} icon={Truck} bg="bg-brand-success text-white" />
        <StatCard label="Cancelados" value={totals.canceled} icon={Prohibit} bg="bg-brand-peach" />
      </div>

      {pendingHandles.length > 0 && (
        <section>
          <h2 className="font-heading text-2xl font-black mb-3">@arrobas com pendências ({pendingHandles.length})</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {pendingHandles.map(h => (
              <div key={h.handle} data-testid={`pending-handle-${h.handle}`}
                onClick={() => setHandleFilter(h.handle)}
                className="card-brutal p-4 bg-white cursor-pointer hover:bg-brand-yellow transition">
                <p className="font-heading text-lg font-black">{h.handle}</p>
                <p className="text-sm text-brand-ink2">{h.customer_name || "—"}</p>
                <div className="flex gap-2 mt-2">
                  <span className="badge-brutal !bg-brand-yellow">{h.requests_count} solicitação(ões)</span>
                  <span className="badge-brutal !bg-brand-mint">{h.items_count} itens</span>
                </div>
                <p className="mt-2 font-bold">Total: {brl(h.total)}</p>
                <p className="text-xs text-brand-ink2 mt-1">Mais antigo: {new Date(h.oldest).toLocaleString("pt-BR")}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="border-b-4 border-brand-ink flex gap-2">
        {[
          { k: "pending", label: `Somente pendentes (${totals.pending})` },
          { k: "all", label: `Todas (${requests.length})` },
        ].map(t => (
          <button
            key={t.k}
            data-testid={`admin-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 font-heading font-black border-2 border-b-0 ${tab === t.k ? "bg-brand-pink text-white border-brand-ink" : "bg-white border-brand-ink"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card-brutal p-10 text-center font-bold">Carregando…</div>
      ) : filteredRequests.length === 0 ? (
        <div className="card-brutal p-10 text-center">
          <Package weight="bold" size={40} className="mx-auto" />
          <p className="font-heading text-xl font-black mt-3">Nenhuma solicitação</p>
        </div>
      ) : (
        <div className="card-brutal overflow-x-auto">
          <table className="tbl w-full">
            <thead>
              <tr>
                <th className="w-40">Data</th>
                <th>@arroba</th>
                <th>Cliente</th>
                <th className="w-20 text-right">Itens</th>
                <th className="w-28 text-right">Total</th>
                <th className="w-32">Status</th>
                <th className="w-24 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map(r => {
                const S = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
                return (
                  <tr key={r.id} data-testid={`admin-request-${r.id}`}>
                    <td className="text-sm">{new Date(r.created_at).toLocaleString("pt-BR")}</td>
                    <td className="font-bold">{r.handle}</td>
                    <td>{r.customer_name || "—"}</td>
                    <td className="text-right">{r.items_count}</td>
                    <td className="text-right font-bold">{brl(r.total_amount)}</td>
                    <td>
                      <span className={`badge-brutal ${S.color}`}>
                        <S.icon weight="bold" size={12} /> {S.label}
                      </span>
                    </td>
                    <td className="text-right">
                      <button
                        data-testid={`admin-open-${r.id}`}
                        onClick={() => openDetail(r.id)}
                        className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-yellow"
                      >
                        <Eye weight="bold" size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <DetailModal
          request={detail}
          onClose={() => setDetail(null)}
          onChanged={() => { setDetail(null); load(); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, bg }) {
  return (
    <div className={`card-brutal p-4 ${bg} flex items-center gap-3`}>
      <Icon weight="bold" size={28} />
      <div>
        <p className="text-xs font-bold uppercase">{label}</p>
        <p className="font-heading text-2xl font-black">{value}</p>
      </div>
    </div>
  );
}

function DetailModal({ request, onClose, onChanged }) {
  const S = STATUS_STYLE[request.status] || STATUS_STYLE.pending;
  const [tracking, setTracking] = useState(request.tracking_code || "");
  const [carrier, setCarrier] = useState(request.carrier || "Correios");
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const setStatus = async (newStatus) => {
    setSaving(true);
    try {
      await api.put(`/expedition/admin/requests/${request.id}`, { status: newStatus });
      toast.success("Status atualizado");
      onChanged();
    } catch { toast.error("Erro"); }
    finally { setSaving(false); }
  };
  const markShipped = async () => {
    if (!tracking.trim()) {
      if (!window.confirm("Marcar como enviado sem código de rastreio?")) return;
    }
    setSaving(true);
    try {
      await api.put(`/expedition/admin/requests/${request.id}`, {
        status: "shipped",
        tracking_code: tracking.trim() || null,
        carrier: carrier.trim() || null,
      });
      toast.success("Envio confirmado");
      onChanged();
    } catch { toast.error("Erro"); }
    finally { setSaving(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancelar esta solicitação? Os itens voltarão a ficar disponíveis para o cliente.")) return;
    setSaving(true);
    try {
      await api.delete(`/expedition/admin/requests/${request.id}`);
      toast.success("Solicitação cancelada");
      onChanged();
    } catch { toast.error("Erro"); }
    finally { setSaving(false); }
  };

  const a = request.address || {};

  return (
    <div className="fixed inset-0 bg-brand-ink/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div data-testid="admin-detail-modal" className="card-brutal w-full max-w-3xl bg-white p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4">
          <div>
            <p className="label-brutal">Solicitação {request.id}</p>
            <h2 className="font-heading text-2xl font-black">{request.handle} · {request.customer_name}</h2>
            <div className="mt-1 flex gap-2 items-center">
              <span className={`badge-brutal ${S.color}`}><S.icon weight="bold" size={12} /> {S.label}</span>
              <span className="text-sm text-brand-ink2">{new Date(request.created_at).toLocaleString("pt-BR")}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 border-2 border-brand-ink"><X weight="bold" size={16} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="card-brutal p-3 bg-brand-cream">
            <p className="label-brutal mb-2 flex items-center gap-1"><MapPin weight="bold" size={12} /> Endereço</p>
            <p className="font-bold">{a.recipient_name || "—"}</p>
            <p>{a.street || ""} {a.number || ""} {a.complement ? ` - ${a.complement}` : ""}</p>
            <p>{a.neighborhood || ""}</p>
            <p>{a.city || ""} {a.state ? ` - ${a.state}` : ""} {a.postal_code ? ` · CEP: ${a.postal_code}` : ""}</p>
            {a.recipient_phone && <p className="text-sm mt-1">Tel: {a.recipient_phone}</p>}
          </div>
          <div className="card-brutal p-3 bg-brand-cream">
            <p className="label-brutal mb-2">Resumo</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><strong>{request.items_count}</strong> item(ns)</div>
              <div><strong>{brl(request.total_amount)}</strong></div>
              {request.shipped_at && <div className="col-span-2">Enviado em: {new Date(request.shipped_at).toLocaleString("pt-BR")}</div>}
            </div>
            {request.notes && <p className="text-sm mt-2 text-brand-ink2">Obs.: {request.notes}</p>}
          </div>
        </div>

        <div className="card-brutal overflow-hidden mb-4">
          <table className="tbl w-full">
            <thead>
              <tr>
                <th className="w-16">Foto</th>
                <th className="w-24">Código</th>
                <th>Descrição</th>
                <th>Live</th>
                <th className="w-24 text-sm">Data live</th>
                <th className="w-16 text-right">Qtd</th>
                <th className="w-28 text-right">Preço</th>
                <th className="w-28 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {(request.orders || []).map(o => (
                <tr key={o.id} data-testid={`detail-item-${o.id}`}>
                  <td className="text-center">
                    {o.photo_url ? (
                      <button
                        type="button"
                        data-testid={`detail-photo-${o.id}`}
                        onClick={() => setLightbox(o.photo_url)}
                        className="w-12 h-12 border-2 border-brand-ink bg-white overflow-hidden inline-flex items-center justify-center hover:ring-2 hover:ring-brand-pink"
                        title="Ver foto"
                      >
                        <img src={o.photo_url} alt={o.description || "peça"} className="w-full h-full object-cover" />
                      </button>
                    ) : (
                      <span
                        className="w-12 h-12 border-2 border-dashed border-brand-ink2 inline-flex items-center justify-center text-brand-ink2"
                        title="Sem foto"
                      >
                        <ImageIcon weight="bold" size={16} />
                      </span>
                    )}
                  </td>
                  <td className="font-bold">{o.product_code || "—"}</td>
                  <td>{o.description || "—"}</td>
                  <td className="text-sm">{o.live_name || "—"}</td>
                  <td className="text-sm">{o.live_date ? fmtDate(o.live_date) : "—"}</td>
                  <td className="text-right">{o.quantity || 1}</td>
                  <td className="text-right">{brl(o.price)}</td>
                  <td className="text-right font-bold">{brl(o.price * (o.quantity || 1))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {request.status === "pending" && (
          <div className="card-brutal p-4 bg-brand-yellow">
            <p className="label-brutal mb-3">Confirmar envio</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label-brutal">Transportadora</label>
                <input data-testid="detail-carrier-input" className="input-brutal" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
              </div>
              <div>
                <label className="label-brutal">Código de rastreio</label>
                <input data-testid="detail-tracking-input" className="input-brutal" value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="BR123456789BR" />
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 pt-4 border-t-4 border-brand-ink">
          <button
            data-testid="detail-picking-list-btn"
            type="button"
            onClick={async () => {
              try {
                const resp = await api.get(`/expedition/admin/requests/${request.id}/picking-list.pdf`, {
                  responseType: "blob",
                  params: { _: Date.now() },
                  headers: { "Cache-Control": "no-cache" },
                });
                const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
                window.open(url, "_blank");
                setTimeout(() => URL.revokeObjectURL(url), 30000);
              } catch { toast.error("Falha ao gerar relatório de peças"); }
            }}
            className="btn-white"
            title="Gerar PDF com foto, descrição e data da live de cada peça"
          >
            <FilePdf weight="bold" size={16} /> Relatório de peças
          </button>
          <button
            data-testid="detail-print-label-btn"
            type="button"
            onClick={async () => {
              try {
                const resp = await api.get(`/expedition/admin/requests/${request.id}/label.pdf`, {
                  responseType: "blob",
                  params: { _: Date.now() },
                  headers: { "Cache-Control": "no-cache" },
                });
                const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
                window.open(url, "_blank");
                setTimeout(() => URL.revokeObjectURL(url), 30000);
              } catch { toast.error("Falha ao gerar etiqueta"); }
            }}
            className="btn-white"
          >
            <Printer weight="bold" size={16} /> Imprimir etiqueta
          </button>
          {request.status === "shipped" && (request.tracking_code || request.carrier) && (
            <button
              data-testid="detail-notify-whatsapp-btn"
              type="button"
              onClick={() => notifyWhatsapp(request)}
              className="btn-white !bg-brand-mint"
              title={!request.customer_whatsapp && !request.customer_phone
                ? "Cliente sem telefone cadastrado — a mensagem abrirá sem número"
                : undefined}
            >
              <WhatsappLogo weight="bold" size={16} /> Avisar cliente
            </button>
          )}
          {request.status !== "canceled" && (
            <button data-testid="detail-cancel-btn" onClick={cancel} disabled={saving} className="btn-white text-brand-pink">
              <Prohibit weight="bold" size={16} /> Cancelar solicitação
            </button>
          )}
          {request.status === "shipped" && (
            <button data-testid="detail-revert-btn" onClick={() => setStatus("pending")} disabled={saving} className="btn-white">
              <ArrowClockwise weight="bold" size={16} /> Reverter para Pendente
            </button>
          )}
          {request.status === "pending" && (
            <button data-testid="detail-ship-btn" onClick={markShipped} disabled={saving} className="btn-pink">
              <PaperPlaneTilt weight="bold" size={16} /> {saving ? "Salvando…" : "Marcar como enviado"}
            </button>
          )}
        </div>

        {lightbox && (
          <div
            data-testid="admin-photo-lightbox"
            className="fixed inset-0 bg-brand-ink/80 z-[70] flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setLightbox(null)}
                data-testid="admin-photo-lightbox-close"
                className="absolute -top-3 -right-3 bg-white border-2 border-brand-ink p-2 z-10"
              >
                <X weight="bold" size={16} />
              </button>
              <img
                src={lightbox}
                alt="Foto da peça"
                className="max-w-[92vw] max-h-[85vh] object-contain border-4 border-brand-ink bg-white"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
